import { type ReactNode } from 'react'
import { IconButton } from '@/components/IconButton'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'

export interface UserManagementTableMenuProps {
  /**
   * Replaces the default "more" button that opens the menu.
   */
  button?: ReactNode
  /**
   * Whether no workspace is selected, the viewer is a platform admin and at
   * least one user is selected.
   */
  canManageGlobalCapacity: boolean
  /**
   * Whether a workspace is selected and at least one user is selected.
   */
  showWorkspaceActions: boolean
  isShowingInactiveUsers: boolean
  onInvite: () => void
  onManageCapacity: () => void
  onRestore: () => void
  onEditRoles: () => void
  onManageSkills: () => void
  onRemove: () => void
}

export function UserManagementTableMenu({
  button,
  canManageGlobalCapacity,
  showWorkspaceActions,
  isShowingInactiveUsers,
  onInvite,
  onManageCapacity,
  onRestore,
  onEditRoles,
  onManageSkills,
  onRemove,
}: UserManagementTableMenuProps) {
  return (
    <Dropdown
      autoDismiss
      position="bottom-end"
      button={
        button ?? (
          <Tooltip content="Actions" placement="bottom">
            <IconButton
              name="more"
              label="More actions"
              testId="user-management-table-menu"
              color="var(--grey600)"
            />
          </Tooltip>
        )
      }
    >
      <DropdownTitle>Actions</DropdownTitle>
      <DropdownItem
        onPress={onInvite}
        icon={<Icon name="add" color="var(--grey600)" />}
        buttonTestId="user-management-table-menu-invite-to-workspace"
      >
        Invite to workspace
      </DropdownItem>
      {canManageGlobalCapacity && (
        <DropdownItem
          onPress={onManageCapacity}
          icon={<Icon name="edit-outlined" color="var(--grey600)" />}
          buttonTestId="user-management-table-row-actions-manage-global-capacity"
          muteClickEvent
        >
          Manage global capacity
        </DropdownItem>
      )}

      {/* Only display these actions when filtering by a workspace and at least one user has been selected. */}
      {showWorkspaceActions && (
        <>
          {isShowingInactiveUsers ? (
            <DropdownItem
              onPress={onRestore}
              icon={<Icon name="add-people" color="var(--grey600)" />}
              buttonTestId="user-management-table-menu-restore-to-workspace"
            >
              Restore to workspace
            </DropdownItem>
          ) : (
            <>
              <DropdownItem
                onPress={onManageCapacity}
                icon={<Icon name="edit-outlined" color="var(--grey600)" />}
                buttonTestId="user-management-table-menu-manage-capacity"
              >
                Manage capacity
              </DropdownItem>
              <DropdownItem
                onPress={onEditRoles}
                icon={<Icon name="edit-roles" color="var(--grey600)" />}
                buttonTestId="user-management-table-menu-edit-roles"
              >
                Edit roles
              </DropdownItem>
              <DropdownItem
                onPress={onManageSkills}
                icon={<Icon name="edit-skills" color="var(--grey600)" />}
                buttonTestId="user-management-table-menu-manage-skills"
              >
                Manage skills
              </DropdownItem>
              <DropdownItem
                onPress={onRemove}
                icon={<Icon name="close" color="var(--grey600)" />}
                buttonTestId="user-management-table-menu-remove-from-workspace"
              >
                Remove from workspace
              </DropdownItem>
            </>
          )}
        </>
      )}
    </Dropdown>
  )
}
